
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { listsApi } from '../utils/listsApi';
import './AnimalCollectionMenu.css';

const API_URL = (
  process.env.REACT_APP_API_URL || ''
).replace(/\/+$/, '');

function AnimalCollectionMenu({ animalId }) {
  const buttonRef = useRef(null);
  const panelRef = useRef(null);
  const requestIdRef = useRef(0);

  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({});
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [lists, setLists] = useState([]);
  const [listIds, setListIds] = useState([]);
  const [isFavorite, setIsFavorite] = useState(false);

  const token = localStorage.getItem('token');

  // Calculate dropdown position relative to the button.
  function calculatePosition() {
    if (!buttonRef.current) return {};

    const rect = buttonRef.current.getBoundingClientRect();

    const width = Math.min(240, window.innerWidth - 16);

    const left = Math.max(
      8,
      Math.min(
        rect.right - width,
        window.innerWidth - width - 8
      )
    );

    const spaceBelow = window.innerHeight - rect.bottom - 12;
    const spaceAbove = rect.top - 12;

    const showAbove =
      spaceBelow < 230 && spaceAbove > spaceBelow;

    const availableSpace = showAbove
      ? spaceAbove
      : spaceBelow;

    const maxHeight = Math.max(
      60,
      Math.min(320, availableSpace)
    );

    return {
      left,
      width,
      maxHeight,
      ...(showAbove
        ? { bottom: window.innerHeight - rect.top + 6 }
        : { top: rect.bottom + 6 })
    };
  }

  // Close when clicking outside, scrolling, resizing,
  // or pressing Escape.
  useEffect(() => {
    if (!open) return;

    function handleOutsideClick(event) {
      if (buttonRef.current?.contains(event.target)) {
        return;
      }

      if (panelRef.current?.contains(event.target)) {
        return;
      }

      setOpen(false);
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    }

    function handleScroll(event) {
      // Allow scrolling inside the dropdown itself.
      if (panelRef.current?.contains(event.target)) {
        return;
      }

      setOpen(false);
    }

    function handleResize() {
      setOpen(false);
    }

    document.addEventListener('pointerdown', handleOutsideClick);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('scroll', handleScroll, true);
    window.addEventListener('resize', handleResize);

    return () => {
      document.removeEventListener(
        'pointerdown',
        handleOutsideClick
      );
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener(
        'scroll',
        handleScroll,
        true
      );
      window.removeEventListener('resize', handleResize);
    };
  }, [open]);

  // Ignore unfinished membership requests if unmounted.
  useEffect(() => {
    return () => {
      requestIdRef.current += 1;
    };
  }, []);

  // Open menu and load collection membership.
  async function handleOpen(event) {
    event.stopPropagation();

    if (open) {
      setOpen(false);
      return;
    }

    setPosition(calculatePosition());
    setOpen(true);
    setError('');

    if (!token) return;

    const requestId = ++requestIdRef.current;

    setLoading(true);
    setLoaded(false);

    try {
      const [availableLists, membership] = await Promise.all([
        listsApi('/lists'),
        listsApi(`/lists/animal/${animalId}/membership`)
      ]);

      if (requestId !== requestIdRef.current) return;

      setLists(availableLists);
      setListIds(membership.list_ids.map(String));
      setIsFavorite(membership.is_favorite);
      setLoaded(true);
    } catch (err) {
      if (requestId === requestIdRef.current) {
        setError(err.message);
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  }

  // Add/remove Favorites.
  async function toggleFavorite() {
    if (busy) return;

    setBusy(true);
    setError('');

    try {
      const response = await fetch(
        isFavorite
          ? `${API_URL}/favorites/${animalId}`
          : `${API_URL}/favorites`,
        {
          method: isFavorite ? 'DELETE' : 'POST',
          headers: {
            token,
            'Content-Type': 'application/json'
          },
          ...(!isFavorite
            ? {
                body: JSON.stringify({
                  animal_id: animalId
                })
              }
            : {})
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.error || 'Unable to update Favorites.'
        );
      }

      setIsFavorite(previous => !previous);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  // Add/remove animal from a custom list.
  async function toggleList(listId) {
    if (busy) return;

    const key = String(listId);
    const alreadyAdded = listIds.includes(key);

    setBusy(true);
    setError('');

    try {
      if (alreadyAdded) {
        await listsApi(
          `/lists/${key}/animals/${animalId}`,
          { method: 'DELETE' }
        );
      } else {
        await listsApi(`/lists/${key}/animals`, {
          method: 'POST',
          body: { animal_id: animalId }
        });
      }

      setListIds(previous =>
        alreadyAdded
          ? previous.filter(id => id !== key)
          : [...previous, key]
      );
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="animal-collection-menu"
      onClick={event => event.stopPropagation()}
    >
      <button
        ref={buttonRef}
        type="button"
        className="animal-menu-button"
        aria-label="Manage animal collections"
        aria-expanded={open}
        aria-haspopup="true"
        onClick={handleOpen}
      >
        •••
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="animal-menu-panel"
          style={position}
          onClick={event => event.stopPropagation()}
          onPointerDown={event => event.stopPropagation()}
        >
          {!token ? (
            <p>
              <Link to="/login">Log in</Link> to manage
              your collections.
            </p>
          ) : loading ? (
            <p>Loading...</p>
          ) : !loaded ? (
            <p className="animal-menu-error" role="alert">
              {error || 'Unable to load collections.'}
            </p>
          ) : (
            <>
              {error && (
                <p className="animal-menu-error" role="alert">
                  {error}
                </p>
              )}

              <label className="animal-menu-option">
                <input
                  type="checkbox"
                  checked={isFavorite}
                  disabled={busy}
                  onChange={toggleFavorite}
                />
                <span>Favorites</span>
              </label>

              <div className="animal-menu-divider" />

              <p className="animal-menu-heading">
                Custom Lists
              </p>

              {lists.map(list => (
                <label
                  key={list.id}
                  className="animal-menu-option"
                >
                  <input
                    type="checkbox"
                    checked={listIds.includes(String(list.id))}
                    disabled={busy}
                    onChange={() => toggleList(list.id)}
                  />

                  <span>{list.name}</span>
                </label>
              ))}

              {lists.length === 0 && (
                <p>
                  No custom lists.{' '}
                  <Link to="/lists">Create one</Link>
                </p>
              )}
            </>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}

export default AnimalCollectionMenu;
